import i18next from "i18next";
import React, { useEffect } from "react";
import Headline from "~/assets/js/components/Typography/Headline";
import OrderConfirmationCutlery from "../Cutlery";
import TypeOfOrder from "../../Checkout/sections/OrderOverview/TypeOfOrder";
import AddressSelected from "../../Checkout/sections/OrderOverview/AddressSelected";
import TippingOptions from "./TippingOptions";
import DeliveryMessage from "./DeliveryMessage";
import ContactInfo from "./ContactInfo";
import OrderDeliverySlot from "~/assets/js/components/OrderDeliverySlot";
import OrderTypeOfPayment from "~/assets/js/components/OrderTypeOfPayment";
import OrderItems from "~/assets/js/components/OrderItems";
import { clearCheckoutSession } from "~/assets/js/Api/storeApi";
import DeliveryInstructions from "./DeliveryInstructions";
import { useUserOrder } from "~/assets/js/Api/userApi";
import isEmpty from "lodash.isempty";
import CartSession from "~/assets/js/global/cartSession";
import MetaPixelUtil from "~/assets/js/Helpers/meta-pixel-util";
import GoogleTagUtil from "~/assets/js/Helpers/google-tag-util";
import * as Matomo from "~/assets/js/Helpers/matomo";
import useGroupOrder from "~/assets/js/global/useGroupOrder";

const OrderConfirmation = () => {
    const cookie = window.cookie.get();
    const { lastOrder } = cookie;
    const isDelivery = cookie?.area?.type === "delivery";
    const { order, isLoading, isError } = useUserOrder(
        lastOrder?.cartUid,
        lastOrder?.requestUid
    );

    console.log(order);
    const { closeGroupOrder, isGroupOrder, isOwner } = useGroupOrder();

    useEffect(() => {
        if (!order) return;

        const cleanupGroupOrder = async ({ isGroupOrder, isOwner }) => {
            if (isGroupOrder && isOwner) {
                await closeGroupOrder();
            }
        };
        cleanupGroupOrder({ isGroupOrder, isOwner });
    }, [order, isGroupOrder, isOwner]);

    // Once you get to this page the order does not exist, remove leftover session data
    useEffect(() => {
        const cleanupSessionsOnThankYou = () => {
            clearCheckoutSession();
            CartSession.clear();
        };
        cleanupSessionsOnThankYou();
    }, []);

    useEffect(() => {
        if (!order || !order.items) return;

        MetaPixelUtil.trackCompleteOrderEvent({
            currency: "EUR",
            value: parseFloat(order.value_gross),
        });
        order.items.forEach((item) => {
            Matomo.addToCart(
                item.product_id,
                item.name,
                item.category,
                item.value_gross,
                item.quantity
            );
        });
        Matomo.trackOrder(
            order.id,
            parseFloat(order.value_gross),
            parseFloat(order.value_net),
            parseFloat(order.value_tax),
            order?.shipping?.value_gross
                ? parseFloat(order.shipping.value_gross)
                : 0,
            false
        );
        window.posthog?.capture("order completed", {
            value: parseFloat(order.value_gross),
            items: order.items.map((item) => {
                return {
                    item_id: String(item.product_id),
                    item_name: item.name,
                    quantity: item.quantity,
                    price:
                        item.value_gross && item.quantity
                            ? parseFloat(item.value_gross) / item.quantity
                            : 0,
                };
            }),
            compensation: {
                level: order.compensation.level,
                value: parseFloat(order.compensation.value_gross),
            },
            storeId: order.store_id,
            groupOrder: order.group_order,
            customerType: order.billing.customer_type,
        });

        GoogleTagUtil.trackCompleteOrderEvent({
            currency: "EUR",
            transaction_id: order.uid,
            value: parseFloat(order.value_gross),
            shipping: order?.shipping?.value_gross
                ? parseFloat(order.shipping.value_gross)
                : 0,
            items: order.items.map((item) => {
                return {
                    item_id: String(item.product_id),
                    item_name: item.name,
                    quantity: item.quantity,
                    price:
                        item.value_gross && item.quantity
                            ? parseFloat(item.value_gross) / item.quantity
                            : 0,
                };
            }),
        });
    }, [order]);

    //if not submitted order redirect to home
    if (isEmpty(lastOrder)) {
        window.location.href = "/";
        return;
    }

    if (isLoading || isError) {
        return null;
    }

    const {
        shipping: shippingAddress,
        billing: billingAddress,
        id,
        items: orderItems,
        payment,
        day_uid,
        cutlery,
        slot_begin,
        slot_end,
        store_id,
    } = order;

    const displayDeliveryOptions =
        isDelivery &&
        (shippingAddress.contactless_delivery ||
            shippingAddress.delivery_phone_call ||
            shippingAddress.give_porter ||
            shippingAddress.no_doorbell);

    return (
        <div className="container">
            <div className="order_confirmation">
                <DeliveryMessage
                    slotBegin={slot_begin}
                    slotEnd={slot_end}
                    storeId={store_id}
                />
                <div className="order_confirmation_number">
                    <p>
                        {i18next.t(
                            `orderConfirmation.${
                                isDelivery ? "deliveryMessage" : "pickupMessage"
                            }.orderNumber`
                        )}
                        {day_uid || id}
                    </p>
                </div>

                <Headline size={5} resizable>
                    {i18next.t("orderConfirmation.headline")}
                </Headline>
                <p>{i18next.t("orderConfirmation.notice")}</p>
                <TippingOptions />
                {displayDeliveryOptions && (
                    <DeliveryInstructions shipping={shippingAddress} />
                )}
                {cutlery && <OrderConfirmationCutlery cutlery={cutlery} />}
                <OrderItems orderItems={orderItems} />
                <ContactInfo />
                <TypeOfOrder edit={false} />
                <OrderDeliverySlot
                    slotBegin={slot_begin}
                    slotEnd={slot_end}
                    earlierAllowed={shippingAddress?.earlier_allowed}
                    isPickup={!isDelivery}
                />

                {isDelivery && (
                    <AddressSelected
                        title="overview.delivery"
                        address={shippingAddress}
                    />
                )}

                <AddressSelected
                    title="overview.billing"
                    address={billingAddress}
                />
                <OrderTypeOfPayment paymentOption={payment.method} />
            </div>
        </div>
    );
};

export default OrderConfirmation;
